import { useEffect, useRef, useState } from 'react';

// Echoes each change locally and emits `onChange` once typing pauses for
// `delay` ms. A new incoming `value` only resyncs the local copy, never emits,
// so a programmatic change (e.g. clearing filters) can't echo back as a query.
// It also cancels an emit still pending from typing: otherwise a Reset inside
// the debounce window would be overwritten by the text it just cleared.
export const useDebouncedChange = <T>(
  value: T,
  onChange: (next: T) => void,
  delay = 500
) => {
  const [local, setLocal] = useState(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setLocal(value);
  }, [value]);

  // Cancel a pending emit if the component unmounts.
  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const change = (next: T) => {
    setLocal(next);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => onChange(next), delay);
  };

  return [local, change] as const;
};
